import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, map } from 'rxjs';
import { ServerConnectionService } from '../server-connection.service';
import { CoreModule } from '../../core.module';
import { ISite } from '../../../types/datatypes';
import { SitesAPIResponse } from '../../../types/responses';

type SiteWebSocketMessage = {
	action: 'update' | 'create' | 'delete';
	data: ISite;
};

@Injectable({
	providedIn: CoreModule,
})
export class SitesService {
	public slugs = {
		FREEBOOKSY_SLUG: 'free-booksy',
		RED_FEATHER_SLUG: 'red-feather-romance',
		BARGAIN_BOOKS_SLUG: 'bargain-booksy',
		NEW_IN_BOOKS_SLUG: 'new-in-books',
		AUDIO_THICKET_SLUG: 'audio-thicket',
		FACEBOOK_ADS_SLUG: 'facebook-ads',
	};

	public LEADMAGNET_SITE_ID = '6a428a8244632e778ec02001';

	public GIVEAWAY_SITE_ID = '64496414b963f141f904412e';

	public RRA_FACEBOOK_SITE_ID = '5f998d3f3b75580011de6600';

	public RRA_AMAZON_SITE_ID = '62702050366323ac5dfddb55';

	private _sitesStore = <ReplaySubject<ISite[]>> new ReplaySubject(1);

	private _sitesBySlug = <ReplaySubject<Record<string, ISite>>> new ReplaySubject(1);

	private _sitesById = <ReplaySubject<Record<string, ISite>>> new ReplaySubject(1);

	private dataStore: {
		store: ISite[];
		bySlug: Record<string, ISite>;
		byId: Record<string, ISite>;
	} = {
			store: [],
			bySlug: {},
			byId: {},
		};

	private facebookOrAmazonAdSites = {
		facebookAds: '5f998d3f3b75580011de6600',
		amazonAds: '62702050366323ac5dfddb55',
	};

	constructor(private scs: ServerConnectionService) {
		this.fetchSites();
	}

	get sitesBySlug$(): Observable<Record<string, ISite>> {
		return this._sitesBySlug.asObservable();
	}

	get sitesStore$(): Observable<ISite[]> {
		return this._sitesStore.asObservable();
	}

	get sitesById$(): Observable<Record<string, ISite>> {
		return this._sitesById.asObservable();
	}

	public getSiteBySlug(slug: string): Observable<ISite> {
		return this.sitesBySlug$.pipe(map((item) => item[slug]));
	}

	public fetchSites(): void {
		this.scs.http$('GET', '/api/sites', null, null).subscribe(
			(result: SitesAPIResponse) => {
				result.sites.forEach((item) => {
					if (item.sitePublic) {
						this.addSite(item);
					}
				});
				this.allDone();
			},
		);
	}

	public addSite(site: ISite): void {
		if (this.dataStore.byId[site._id]) {
			Object.assign(this.dataStore.byId[site._id], site);
		} else {
			this.dataStore.byId[site._id] = site;
			this.dataStore.bySlug[site.seoSlug] = this.dataStore.byId[site._id];
			this.dataStore.store.push(this.dataStore.byId[site._id]);
		}
	}

	public isFacebookOrAmazonAdSite(siteName: string): boolean {
		const siteId = this.dataStore.bySlug[siteName]?._id;

		if (!siteId) return false;

		const facebookOrAmazonAdSiteIdes = Object.values(this.facebookOrAmazonAdSites);

		return facebookOrAmazonAdSiteIdes.includes(siteId);
	}

	public allDone(): void {
		this._sitesById.next({ ...this.dataStore.byId });
		this._sitesBySlug.next({ ...this.dataStore.bySlug });
		this._sitesStore.next(this.dataStore.store.slice());
	}

	public handleWebSocket(data: SiteWebSocketMessage): void {
		if (data.action === 'update' || data.action === 'create') {
			this.addSite(data.data);
		}
		if (data.action === 'delete') {
			this.removeSite(data.data);
		}
	}

	public removeSite(site: ISite): void {
		delete this.dataStore.byId[site._id];
		this.dataStore.store.some(
			(item, index, array) => {
				if (item._id === site._id) {
					array.splice(index, 1);
					return true;
				}

				return false;
			},
		);
		this.allDone();
	}

	public getFriendlySiteNameBySlug(siteName: string): string {
		const site = this.dataStore.bySlug[siteName];
		if (site) {
			return site.name;
		}
		return siteName;
	}

	public getSiteById(siteId: string): ISite {
		return this.dataStore.byId[siteId];
	}
}
